import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import dateUtils from '@bingads-webui-component-legacy/humanize/date';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withCustomValidation } from '@bingads-webui-react/validation';
import {
  Card,
  CardHeader,
  Field,
  Label,
  makeStyles,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react';
import { HelpButton } from '@bingads-webui-react/help-popup';
import {
  DATE_FORMAT,
  MS_PER_DAY,
} from '../../date';
import { errorMessages } from '../../errors';
import { cardHeaderStyles } from '../../page-layout';
import { getDatePickerStrings } from './date-picker-strings';

const useStyles = makeStyles(cardHeaderStyles);

export const StartDateCardInternal = ({
  onChangeStartDate,
  showError,
  startDate,
  updateValidation,
}) => {
  const styles = useStyles();
  const i18n = useContext(I18nContext);
  const localizedStrings = getDatePickerStrings(i18n);
  const tomorrow = new Date(Date.now() + MS_PER_DAY);
  const initialDate = useMemo(() => (startDate ? new Date(startDate) : null), [startDate]);
  const learnMore = i18n.getString(_TL_('Click to learn more about'));
  const startDateLabel = i18n.getString(_TL_('Start date'));
  const fieldProps = startDate == null && showError
    ? {
      validationMessage: i18n.getString(errorMessages.START_DATE_REQUIRED),
      validationState: 'error',
    }
    : null;

  const onSelectStartDate = useCallback((date) => {
    const formattedDate = dateUtils.format(date, DATE_FORMAT);
    updateValidation([], { startDate: formattedDate });
    onChangeStartDate(formattedDate);
  }, [onChangeStartDate, updateValidation]);

  const onFormatDate = useCallback(date => (
    date
      ? i18n.formatDate(date)
      : ''
  ), [i18n]);

  return (
    <Card className="start-date-card">
      <CardHeader
        header={
          <div className={styles.cardHeader}>
            <Label size="large" className={styles.label} required>{startDateLabel}</Label>
            <HelpButton
              aria-label={`${learnMore} ${startDateLabel}`}
              topic="pop_BA_Experiments_StartDate"
            />
          </div>
          }
      />
      <Field {...fieldProps}>
        <DatePicker
          ariaLabel={i18n.getString(_TL_('Lift measurement start date picker'))}
          formatDate={onFormatDate}
          initialPickerDate={tomorrow}
          minDate={tomorrow}
          onSelectDate={onSelectStartDate}
          strings={localizedStrings}
          value={initialDate}
        />
      </Field>
    </Card>
  );
};

StartDateCardInternal.propTypes = {
  onChangeStartDate: PropTypes.func.isRequired,
  showError: PropTypes.bool,
  startDate: PropTypes.string,
  updateValidation: PropTypes.func.isRequired,
};

StartDateCardInternal.defaultProps = {
  showError: false,
  startDate: null,
};

export const StartDateCard = withCustomValidation(StartDateCardInternal);
